// src/utils/sat/satPlanAi.js
//
// Frontend wrapper around the generateSatPlanDraft Gemini callable.
//
// The deterministic skeleton (from planEngine.generateSatPlanSkeleton) is the
// source of truth. The AI only adds language: a parent-facing summary, the
// reasoning, and per-item tutor guidance. We merge guidance back BY ID so the
// skeleton's ordering/allocation can never be altered by the model, and we fall
// back to a local template if the callable errors (so the feature never fully
// fails).

import { httpsCallable } from "firebase/functions";
import { functions as firebaseFunctions } from "../../firebaseConfig";

function localGuidance(item) {
  return (
    `Target ${item.subcategory} in ${item.majorCategory}. ` +
    `Identify the specific error pattern, teach the core method, then drill mixed problems` +
    (item.allocatedHours ? ` across about ${item.allocatedHours}h` : "") +
    " until accuracy is consistent."
  );
}

function localWrittenPlan(skeleton, context) {
  const studentName = (context && context.studentName) || "The student";
  const totalHours = skeleton.totalHours || (context && context.totalHours) || 0;
  const focus = (skeleton.checklist || [])
    .filter((it) => !it.isStretch)
    .slice(0, 3)
    .map((it) => it.subcategory);
  const subjectLine = (skeleton.subjects || [])
    .filter((s) => (s.allocatedHours || 0) > 0)
    .map((s) => `${s.subject}: ${s.allocatedHours}h`)
    .join(", ");

  return {
    parentSummary:
      `${studentName}'s ${totalHours}-hour SAT plan focuses time on the weakest diagnostic areas first` +
      (subjectLine ? ` (${subjectLine})` : "") +
      (focus.length ? `, starting with ${focus.join(", ")}` : "") +
      ". As those improve, sessions move to the next-weakest skills.",
    reasoning:
      "Priority follows the diagnostic: lowest-accuracy major categories first, and within each, the weakest subcategories first. " +
      "Hours lean toward the weaker subject while keeping coverage of both, then spread across skills by how much improvement each needs. " +
      "Items beyond the agreed hours are listed as stretch goals and are not counted in the plan total.",
  };
}

/** Attach guidance + written plan onto the skeleton checklist (pure merge). */
function applyDraft(skeleton, draft, context) {
  const byId = new Map(
    (draft && Array.isArray(draft.items) ? draft.items : []).map((it) => [String(it.id), it.guidance])
  );

  const checklist = (skeleton.checklist || []).map((it) => ({
    ...it,
    guidance: byId.get(String(it.id)) || localGuidance(it),
  }));

  const fallbackWritten = localWrittenPlan(skeleton, context);

  return {
    ...skeleton,
    checklist,
    writtenPlan: {
      parentSummary: (draft && draft.parentSummary) || fallbackWritten.parentSummary,
      reasoning: (draft && draft.reasoning) || fallbackWritten.reasoning,
    },
    aiProvider: (draft && draft.provider) || "local",
  };
}

/**
 * Generate a draft plan: skeleton -> AI prose merged by id.
 * Never throws — on error returns a fully-usable local draft.
 *
 * @returns {{ ...skeleton, checklist (with guidance), writtenPlan, aiProvider, aiError? }}
 */
export async function generatePlanDraft({ skeleton, context }) {
  try {
    const callable = httpsCallable(firebaseFunctions, "generateSatPlanDraft");
    const res = await callable({ skeleton, context: context || {} });
    return applyDraft(skeleton, res.data || {}, context);
  } catch (err) {
    const local = applyDraft(skeleton, null, context);
    return { ...local, aiProvider: "local", aiError: err?.message || "AI generation failed" };
  }
}
